import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';

import * as OrderformActions from './orderform.actions';
import { OrderformService } from '../services/orderform.service';
import {
  catchError,
  debounceTime,
  delay,
  filter,
  map,
  switchMap,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { OrderformFascade } from '../fascades/orderform.fascade';
import { interval, Observable, of } from 'rxjs';
import {
  OrderformOrderState,
  OrderformPaymentProvider,
  OrderformRedirectResponse,
  OrderStatusResponse,
} from '@orderform/widgets';
import { OrderformIframeService } from '../services/orderform-iframe.service';
import { HttpClient } from '@angular/common/http';
import { ToasterService } from '@digistore/ds24-ui/toaster';
import { CustomErrorMessageService } from '../components/payment-methods/services/custom-error-messages.service';
import { OrderformLocalStorageService } from '../services/orderform-local-storage.service';
import { hasValidationFailure, mapValidationCodeToFieldNames } from '../misc/validation-code-mapper';
import { ClientActionType, OrderStatus, PAYMENT_ERROR_MESSAGES } from './orderform.models';
import { ApiService } from '../services/api.service';
const checkRequestResult = (
  result: OrderformOrderState | OrderformRedirectResponse,
  successCallback,
  defaultAction
) => {
  const orderState = result as OrderformOrderState;
  const paymentStatus = orderState?.order?.payment_provider?.status?.status;
  const hasErrorMessages = !!orderState?.messages?.errors?.length;
  const isFailureStatus = paymentStatus === 'refused' || paymentStatus === 'error' || paymentStatus === 'uncertain';

  if ((result as any).paymentUrl) {
    successCallback();
    return OrderformActions.sendOrderFormRequestPaymentUrlResponse({
      url: (result as any).paymentUrl,
    });
  }
  // Handle redirects from BE
  else if ((result as OrderformRedirectResponse).action === 'redirect') {
    successCallback();
    return OrderformActions.sendOrderFormRequestRedirect({
      data: result as OrderformRedirectResponse,
    });
  } else if ((result as OrderformRedirectResponse).action === 'show_message') {
    return OrderformActions.sendOrderFormResponseShowMessage({
      data: result as OrderformRedirectResponse,
    });
  } else if (
    (result as OrderformOrderState).order.payment_provider.status?.status === 'pending' &&
    (result as OrderformOrderState).order.purchase_id
  ) {
    return OrderformActions.sendOrderFormRequestPaymentPending({
      purchaseId: (result as OrderformOrderState).order.purchase_id,
    });
  } else if (orderState?.order?.purchase_id && !isFailureStatus && !hasErrorMessages) {
    return OrderformActions.sendOrderFormRequestPaymentPending({
      purchaseId: orderState.order.purchase_id,
    });
  }

  successCallback();
  return defaultAction;
};

@Injectable()
export class OrderformEffects {
  constructor(
    private _actions$: Actions,
    private _orderformService: OrderformService,
    private _orderformFascade: OrderformFascade,
    private _orderformIframeService: OrderformIframeService,
    private _httpClient: HttpClient,
    private _toasterService: ToasterService,
    private _customErrorMessageService: CustomErrorMessageService,
    private _localStorageService: OrderformLocalStorageService,
    private _apiService: ApiService
  ) {}

  onChangeUpdate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        OrderformActions.updateProductQuantity,
        OrderformActions.updateProductETicketId,
        OrderformActions.updateProductVariantId,
        OrderformActions.updateProductCustomForm
      ),
      debounceTime(50),
      withLatestFrom(this._orderformFascade.order$),
      map(([, data]) => {
        return OrderformActions.updateOrderformRequest({ data: data.order });
      })
    )
  );

  /**
   * update
   */
  updateOrderformRequest$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OrderformActions.updateOrderformRequest),
      switchMap((action) => {
        return this._orderformService.update$(action.data).pipe(
          map((result: any) => {
            return checkRequestResult(result, () => {}, OrderformActions.updateOrderformSuccess({ data: result }));
          }),
          catchError((error) => {
            return of(OrderformActions.updateOrderformError(error));
          })
        );
      })
    )
  );

  /**
   * Validate
   */
  validateRequest$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OrderformActions.validateOrderformRequest),
      switchMap((action) =>
        this._orderformService.validate$(action.data).pipe(
          map((result) => OrderformActions.validateOrderformRequestSuccess({ data: result })),
          catchError((error) => of(OrderformActions.validateOrderformRequestError(error)))
        )
      )
    )
  );

  /**
   * Submit complete form data
   */
  sendOrderFormRequest$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OrderformActions.sendOrderFormRequest),
      switchMap((action) =>
        this._orderformService.send$(action.data).pipe(
          map((result: any) => {
            // Check if it's async order processing response (has purchase_id and status)
            if (result.purchase_id && result.status === 'processed') {
              return OrderformActions.sendOrderFormRequestPolling({
                orderId: result.purchase_id,
              });
            }

            return checkRequestResult(
              result,
              () => {},
              OrderformActions.sendOrderFormRequestValidationError({
                data: result as OrderformOrderState,
              })
            );
          }),
          catchError((error) => of(OrderformActions.sendOrderFormRequestError(error)))
        )
      )
    )
  );

  pollOrderStatus$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OrderformActions.sendOrderFormRequestPolling),
      delay(500),
      switchMap(({ orderId }) => {
        return interval(1000).pipe(
          switchMap(() =>
            this._httpClient.get<OrderStatusResponse>(`/api/payment/order-status?orderId=${orderId}`).pipe(
              catchError((error) => {
                // 404 means order is still being processed, continue polling
                if (error.status === 404) {
                  return of({ type: '[Orderform] Order Still Processing' } as const);
                }

                // For other errors, also continue polling for now
                return of({ type: '[Orderform] Order Still Processing' } as const);
              })
            )
          ),
          map((status: OrderStatusResponse | { type: string }) => {
            const orderStatus = status as OrderStatusResponse;

            // Check if this is a "still processing" marker
            if ((status as { type: string }).type === '[Orderform] Order Still Processing') {
              return status as { type: string };
            }

            // Check if validation failed
            if (
              orderStatus.status === OrderStatus.VALIDATION_FAILED ||
              (orderStatus.validations && hasValidationFailure(orderStatus.validations))
            ) {
              // Check if we can map to specific field names for scroll-to-field functionality
              const fieldNames = orderStatus.failure_codes
                ? Object.entries(orderStatus.failure_codes)
                    .filter(([_, code]) => code !== null)
                    .flatMap(([category, code]) => mapValidationCodeToFieldNames(category, code))
                : [];

              if (fieldNames.length > 0) {
                return OrderformActions.sendOrderFormRequestPollingFieldValidationFailed({
                  response: orderStatus,
                  fieldNames,
                });
              }

              return OrderformActions.sendOrderFormRequestPollingValidationFailed({
                response: orderStatus,
              });
            }

            const clientAction = orderStatus.client_action_payload;

            if (clientAction?.action_type === ClientActionType.REDIRECT_IFRAME && clientAction.redirect_url) {
              return OrderformActions.sendOrderFormRequestThreeDSecureRedirect({
                url: clientAction.redirect_url,
                orderId: orderStatus.order_id,
              });
            }
            if (clientAction?.action_type === ClientActionType.REDIRECT_FULL_PAGE && clientAction.redirect_url) {
              return OrderformActions.sendOrderFormRequestRedirect({
                data: {
                  action: 'redirect',
                  url: clientAction.redirect_url,
                  headline: '',
                  message: '',
                },
              });
            }
            if (clientAction?.action_type === ClientActionType.CLIENT_SDK_SUBMIT) {
              return OrderformActions.sendOrderFormRequestClientSdkSubmit({
                orderId: orderStatus.order_id,
              });
            }

            // Terminal failure — stop polling, show error
            if (
              (
                [OrderStatus.PAYMENT_FAILED, OrderStatus.CREATION_FAILED, OrderStatus.RISK_ANALYSIS_FAILED] as string[]
              ).includes(orderStatus.status)
            ) {
              return OrderformActions.sendOrderFormRequestPollingPaymentFailed();
            }

            return { type: '[Orderform] Order Still Processing' } as const;
          }),
          takeWhile((action) => (action as { type: string }).type === '[Orderform] Order Still Processing', true)
        );
      })
    )
  );

  pollOrderStatusAfterClientAction$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OrderformActions.sendOrderFormRequestPollingAfterClientAction),
      delay(500),
      switchMap(({ orderId }) =>
        interval(1000).pipe(
          switchMap(() =>
            this._httpClient.get<OrderStatusResponse>(`/api/payment/order-status?orderId=${orderId}`).pipe(
              catchError(() => {
                return of({ type: '[Orderform] Order Still Processing' } as const);
              })
            )
          ),
          map((status: OrderStatusResponse | { type: string }) => {
            const orderStatus = status as OrderStatusResponse;

            if ((status as { type: string }).type === '[Orderform] Order Still Processing') {
              return status as { type: string };
            }

            // Validation failures
            if (
              orderStatus.status === OrderStatus.VALIDATION_FAILED ||
              (orderStatus.validations && hasValidationFailure(orderStatus.validations))
            ) {
              const fieldNames = orderStatus.failure_codes
                ? Object.entries(orderStatus.failure_codes)
                    .filter(([_, code]) => code !== null)
                    .flatMap(([category, code]) => mapValidationCodeToFieldNames(category, code))
                : [];

              if (fieldNames.length > 0) {
                return OrderformActions.sendOrderFormRequestPollingFieldValidationFailed({
                  response: orderStatus,
                  fieldNames,
                });
              }

              return OrderformActions.sendOrderFormRequestPollingValidationFailed({
                response: orderStatus,
              });
            }

            // Payment still processing — keep polling
            if (orderStatus.status === OrderStatus.PAYMENT_REQUESTED) {
              return { type: '[Orderform] Order Still Processing' } as const;
            }

            // Order completed — navigate directly to completion page
            // (bypasses redirect$ effect which intercepts Primer redirects for card submit)
            if (
              ([OrderStatus.PAID, OrderStatus.COMPLETED] as string[]).includes(orderStatus.status) &&
              orderStatus.client_action_payload?.redirect_url
            ) {
              window.location.href = orderStatus.client_action_payload.redirect_url;
              return { type: '[Orderform] Redirecting' } as const;
            }

            // Terminal failure — stop polling, show error
            if (
              (
                [OrderStatus.PAYMENT_FAILED, OrderStatus.CREATION_FAILED, OrderStatus.RISK_ANALYSIS_FAILED] as string[]
              ).includes(orderStatus.status)
            ) {
              return OrderformActions.sendOrderFormRequestPollingPaymentFailed();
            }

            return { type: '[Orderform] Order Still Processing' } as const;
          }),
          takeWhile((action) => (action as { type: string }).type === '[Orderform] Order Still Processing', true)
        )
      )
    )
  );

  redirect$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(OrderformActions.sendOrderFormRequestRedirect),
        withLatestFrom(this._orderformFascade.order$),
        delay(500),
        tap(([action, orderState]) => {
          // Check if payment provider is Primer
          const rendererType = orderState.order.payment_provider?.renderer_type;
          const isPrimer =
            rendererType === 'primer' || rendererType === 'primer_paypal' || rendererType === 'primer_apple_google_pay';

          // Handle special Primer case if needed
          if (isPrimer) {
            this._orderformFascade.primerRedirectIntercepted$.emit({
              redirectData: action.data,
            });

            return;
          }

          if (window !== window.parent) {
            // iframe
            const payload = {
              redirect_id: action.data.redirect_id,
              url: action.data.url,
              delay_ms: 0,
            };

            this._orderformIframeService.ds24_sendIframeMsg('redirect', payload);
          } else {
            window.location.href = action.data.url;
          }
        })
      ),
    { dispatch: false }
  );

  clientSdkSubmit$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(OrderformActions.sendOrderFormRequestClientSdkSubmit),
        tap(({ orderId }) => {
          this._orderformFascade.clientSdkSubmitRequested$.emit({ orderId });
        })
      ),
    { dispatch: false }
  );

  /**
   * Open 3DS challenge in a modal iframe overlay.
   *
   * Polling stops when this action is dispatched (takeWhile terminates).
   * When the customer completes 3DS, the callback page sends a postMessage,
   * we tear down the overlay and re-dispatch sendOrderFormRequestPolling
   * so polling resumes and picks up the receipt redirect.
   */
  threeDSecureRedirect$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OrderformActions.sendOrderFormRequestThreeDSecureRedirect),
      switchMap(({ url, orderId }) => {
        // Prevent duplicate modals
        const existing = document.getElementById('ds24-3ds-overlay');
        if (existing) {
          existing.remove();
        }

        // --- Overlay ---
        const overlay = document.createElement('div');
        overlay.id = 'ds24-3ds-overlay';
        overlay.style.cssText = [
          'position:fixed',
          'inset:0',
          'z-index:2147483647',
          'background:rgba(0,0,0,0.6)',
          'display:flex',
          'align-items:center',
          'justify-content:center',
        ].join(';');

        // --- Modal container ---
        const modal = document.createElement('div');
        modal.style.cssText = [
          'background:#fff',
          'border-radius:12px',
          'overflow:hidden',
          'width:min(480px,90vw)',
          'height:min(640px,85vh)',
          'display:flex',
          'flex-direction:column',
          'box-shadow:0 8px 32px rgba(0,0,0,0.3)',
        ].join(';');

        // --- Header ---
        const header = document.createElement('div');
        header.style.cssText = [
          'padding:12px 16px',
          'background:#f8f8f8',
          'border-bottom:1px solid #e0e0e0',
          'font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif',
          'font-size:14px',
          'color:#333',
          'text-align:center',
          'flex-shrink:0',
        ].join(';');
        header.textContent = 'Payment verification';

        // --- Iframe ---
        const iframe = document.createElement('iframe');
        iframe.src = url;
        iframe.style.cssText = 'flex:1;width:100%;border:none;';
        iframe.setAttribute('allow', 'payment');

        modal.appendChild(header);
        modal.appendChild(iframe);
        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        // Return Observable that emits when postMessage signals 3DS completion,
        // then re-starts polling so the next cycle picks up the receipt redirect.
        return new Observable<Action>((subscriber) => {
          const onMessage = (event: MessageEvent): void => {
            if (event.data?.type === 'ds24_3ds_complete') {
              window.removeEventListener('message', onMessage);
              overlay.remove();
              subscriber.next(OrderformActions.sendOrderFormRequestPolling({ orderId }));
              subscriber.complete();
            }
          };
          window.addEventListener('message', onMessage);

          // Cleanup if effect is torn down (e.g. another 3DS action arrives)
          return () => {
            window.removeEventListener('message', onMessage);
            overlay.remove();
          };
        });
      })
    )
  );

  checkDoubleOrderRequest$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OrderformActions.checkDoubleOrderRequest),
      withLatestFrom(this._orderformFascade.config$),
      switchMap(([action, config]) => {
        return this._orderformService
          .checkDoubleOrder(config.ajax_key, action.email)
          .pipe(map((data) => OrderformActions.checkDoubleOrderResponse({ data })));
      }),
      catchError((error) => {
        return of(OrderformActions.checkDoubleOrderError(error));
      })
    )
  );

  callGenericAjaxUrlRequest$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(OrderformActions.callGenericAjaxUrlRequest),
        switchMap((action) =>
          this._httpClient.get<{ debug_info: string; message?: string }>(action.ajaxUrl).pipe(
            filter((result) => !!result.message),
            tap((result) => this._toasterService.success(result.message.replace(/\|/g, '<br>')))
          )
        )
      ),
    { dispatch: false }
  );

  showMessage$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(OrderformActions.sendOrderFormResponseShowMessage),
        tap((result) => {
          const tmp = document.createElement('DIV');
          tmp.innerHTML = result.data.message;
          this._toasterService.error(tmp.textContent || tmp.innerText || '');
        })
      ),
    { dispatch: false }
  );

  /**
   * Handle validation failure from order status polling.
   * Shows error messages to the user when async order validation fails.
   */
  orderStatusPollingValidationFailed$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(OrderformActions.sendOrderFormRequestPollingValidationFailed),
        tap(({ response }) => {
          // Get the first non-null failure reason to display
          const failureReasons = response.failure_reasons;
          for (const [_category, reason] of Object.entries(failureReasons)) {
            if (reason) {
              this._toasterService.error(reason);
              break;
            }
          }
        })
      ),
    { dispatch: false }
  );

  orderStatusPollingPaymentFailed$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(OrderformActions.sendOrderFormRequestPollingPaymentFailed),
        tap(() => {
          this._toasterService.error(PAYMENT_ERROR_MESSAGES.PAYMENT_ERROR_MESSAGE_DEFAULT);
        })
      ),
    { dispatch: false }
  );

  /**
   * Handle field-specific validation failure from order status polling.
   * Scrolls to the first field with an error after a short delay to allow DOM updates.
   */
  orderStatusPollingFieldValidationFailed$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(OrderformActions.sendOrderFormRequestPollingFieldValidationFailed),
        delay(50), // Small delay to allow validation errors to be rendered
        tap(() => {
          this._apiService.scrollToFormError();
        })
      ),
    { dispatch: false }
  );

  paymentProviderBraintreeStatusError$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(OrderformActions.sendOrderFormRequestValidationError),
        map((action) => action.data),
        filter((data: OrderformOrderState) => {
          return !!(
            data.order.payment_provider?.status?.status === 'refused' &&
            data.order.payment_provider?.status?.message &&
            this.isBraintreeProvider(data.order.payment_provider)
          );
        }),
        switchMap(async (data) => {
          const errorCode = this.extractErrorCodeFromMessage(data.order.payment_provider.status.message);
          const errorResult = {
            error: {
              code: errorCode || 'unknown',
              message: data.order.payment_provider.status.message || 'Payment failed',
            },
          };
          const errorMessage = this._customErrorMessageService.getCustomErrorMessageFromOrderData(errorResult, data);
          this._toasterService.error(errorMessage);

          return undefined;
        })
      ),
    { dispatch: false }
  );
  private extractErrorCodeFromMessage(message: string): string | null {
    if (!message) return null;
    const match = message.match(/BTEC-(\d+)|(\d+)/); // Input for example "Declined - Call Issuer (Fehlercode: BTEC-2044-51)"" output match[0] = "BTEC-2044-51" or match[1] = "2044"
    return match ? match[1] || match[2] : null;
  }
  private isBraintreeProvider(paymentProvider: OrderformPaymentProvider): boolean {
    return paymentProvider && paymentProvider.renderer_type === 'braintree';
  }

  /**
   * Auto-save to localStorage when order data changes
   * Debounce to avoid too frequent saves
   */
  saveProgressToLocalStorage$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(OrderformActions.saveProgressToLocalStorage),
        debounceTime(500),
        withLatestFrom(this._orderformFascade.order$),
        tap(([, order]) => {
          if (!this._localStorageService.isLocalStorageAvailable()) {
            return;
          }

          const progressData = {
            shipping_address: order.order.shipping_address,
            billing_address: order.order.billing_address,
            has_billing_address: order.order.has_billing_address,
          };

          this._localStorageService.saveProgress(progressData);
        })
      ),
    { dispatch: false }
  );

  /**
   * Trigger save when shipping or billing address changes
   */
  triggerSaveOnAddressChange$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OrderformActions.setToOrderStore, OrderformActions.updateOrderformSuccess),
      map(() => OrderformActions.saveProgressToLocalStorage())
    )
  );

  /**
   * Clear localStorage after successful order submission
   */
  clearProgressAfterSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(OrderformActions.sendOrderFormRequestPaymentUrlResponse, OrderformActions.sendOrderFormRequestRedirect),
        tap(() => {
          this._localStorageService.clearProgress();
        })
      ),
    { dispatch: false }
  );

  /**
   * Clear localStorage on explicit action
   */
  clearProgressFromLocalStorage$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(OrderformActions.clearProgressFromLocalStorage),
        tap(() => {
          this._localStorageService.clearProgress();
        })
      ),
    { dispatch: false }
  );
}
